import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Building, 
  Briefcase, 
  Phone, 
  Mail, 
  Edit, 
  Trash2, 
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { interviewService, Interview, InterviewFormData } from '../../services/interviewService';
import { useAuthStore } from '../../stores/authStore';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const InterviewManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [filteredInterviews, setFilteredInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0,
    today: 0,
    thisWeek: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterInterviews();
  }, [interviews, searchTerm, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Per ora carica solo le statistiche base
      setInterviews([]);
      setStats({
        total: 0,
        scheduled: 0,
        completed: 0,
        cancelled: 0,
        today: 0,
        thisWeek: 0
      });
      
      // TODO: Implementare quando la collection interviews sarà creata
      // const [allInterviews, interviewStats] = await Promise.all([
      //   interviewService.getAllInterviews(),
      //   interviewService.getInterviewStats()
      // ]);
      // setInterviews(allInterviews);
      // setStats(interviewStats);
      
    } catch (error) {
      console.error('Error loading interviews:', error);
      setError('Errore nel caricamento delle interviste');
    } finally {
      setLoading(false);
    }
  };

  const filterInterviews = () => {
    let filtered = interviews;

    // Filtro per ricerca
    if (searchTerm) {
      filtered = filtered.filter(interview =>
        interview.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro per status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(interview => interview.status === statusFilter);
    }

    setFilteredInterviews(filtered);
  };

  const handleStatusUpdate = async (interviewId: string, newStatus: Interview['status']) => {
    try {
      await interviewService.updateInterviewStatus(interviewId, newStatus);
      await loadData(); // Ricarica i dati
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Errore nell\'aggiornamento dello status');
    }
  };

  const handleDeleteInterview = async (interviewId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa intervista?')) return;
    
    try {
      await interviewService.deleteInterview(interviewId);
      await loadData(); // Ricarica i dati
    } catch (error) {
      console.error('Error deleting interview:', error);
      alert('Errore nell\'eliminazione dell\'intervista');
    }
  };

  const openDetailModal = (interview: Interview) => {
    setSelectedInterview(interview);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setSelectedInterview(null);
    setShowDetailModal(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Calendar className="w-4 h-4 text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'rescheduled':
        return <RefreshCw className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'rescheduled':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'Programmata';
      case 'completed':
        return 'Completata';
      case 'cancelled':
        return 'Cancellata';
      case 'rescheduled':
        return 'Riprogrammata';
      default:
        return 'Sconosciuto';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Caricamento interviste...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestione Interviste</h1>
            <p className="text-sm sm:text-base text-gray-600">Gestisci tutte le interviste degli utenti</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/moderator/interviews/new')}
              className="flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Nuova Intervista</span>
              <span className="sm:hidden">Nuova</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
              <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Totali</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Programmate</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.scheduled}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
              <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Completate</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
              <XCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Cancellate</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.cancelled}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Oggi</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.today}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-indigo-100 rounded-lg">
              <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Settimana</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.thisWeek}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtri e Ricerca */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Cerca per azienda, posizione, candidato..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tutti gli status</option>
              <option value="scheduled">Programmate</option>
              <option value="completed">Completate</option>
              <option value="cancelled">Cancellate</option>
              <option value="rescheduled">Riprogrammate</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista Interviste */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Interviste ({filteredInterviews.length})
          </h2>
        </div>
        
        {/* Stato vuoto */}
        <div className="px-6 py-12 text-center">
          <div className="text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">Nessuna intervista trovata</p>
            <p className="text-sm mb-6">Inizia a gestire le interviste degli utenti creando la prima intervista</p>
            <button
              onClick={() => navigate('/moderator/interviews/new')}
              className="flex items-center mx-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crea Prima Intervista
            </button>
            <p className="text-xs text-gray-400 mt-4">
              💡 Suggerimento: Le interviste ti permettono di tenere traccia dei colloqui degli utenti
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewManagementPage; 